import { Badge } from "@package/subframe/components/Badge";
import {
  FeatherClipboard,
  FeatherFile,
  FeatherImage,
  FeatherPaperclip,
} from "@subframe/core";
import type { ReactNode } from "react";

import {
  ComplianceChecklistItemResultResponseDtoResult as ChecklistResult,
  type ComplianceTaskDefinitionResponseDto,
  type ComplianceTaskFileDto,
  type ComplianceTaskInstanceResponseDto,
} from "@/generated/model";

import {
  downloadableFiles,
  formatCompleterName,
  formatFileSize,
  formatRecordDate,
  formatRecordDateTime,
  hasChecklistResults,
  hasCompletionNotes,
  hasCompletionRecordDetail,
  serviceDetailRows,
} from "./completion-record-view";

export function getChecklistResultBadge(result?: ChecklistResult) {
  switch (result) {
    case ChecklistResult.PASS:
      return <Badge variant="success">Pass</Badge>;
    case ChecklistResult.FAIL:
      return <Badge variant="error">Fail</Badge>;
    case ChecklistResult.NOT_APPLICABLE:
      return <Badge variant="neutral">N/A</Badge>;
    default:
      return <Badge variant="neutral">—</Badge>;
  }
}

export function getFileIcon(mimeType?: string) {
  if (mimeType?.startsWith("image/"))
    return <FeatherImage className="h-4 w-4 text-subtext-color" />;
  return <FeatherFile className="h-4 w-4 text-subtext-color" />;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-caption font-caption text-subtext-color">
        {label}
      </span>
      <span className="text-body font-body text-neutral-900">{value}</span>
    </div>
  );
}

function SectionLabel({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-1.5 text-caption-bold font-caption-bold text-subtext-color">
      {icon}
      {children}
    </span>
  );
}

/**
 * The headline facts of a completed instance. Split from the detail sections
 * because the history page renders these in its own collapsed row header.
 */
export function CompletionRecordFacts({
  instance,
}: {
  instance: ComplianceTaskInstanceResponseDto;
}) {
  const completedAt = formatRecordDateTime(instance.completedAt);
  const completedBy = formatCompleterName(instance.completedBy);
  const nextDue = formatRecordDate(instance.seriesNextDueDate);

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {completedAt && <Fact label="Completed" value={completedAt} />}
      {completedBy && <Fact label="Completed by" value={completedBy} />}
      {nextDue && <Fact label="Next due" value={nextDue} />}
    </div>
  );
}

interface DetailsProps {
  instance: ComplianceTaskInstanceResponseDto;
  definition: ComplianceTaskDefinitionResponseDto;
  /**
   * Signed download URLs expire after five minutes, so a record served from the
   * offline cache carries links that would open a storage error page. Those
   * rows render inert with an explanation instead.
   */
  evidenceLinks?: "live" | "offline";
  /** Trailing node per evidence row — the seam for manager-only file actions. */
  fileActions?: (file: ComplianceTaskFileDto) => ReactNode;
  /** Rendered below every section — the seam for manager-only controls. */
  footer?: ReactNode;
}

/**
 * Everything captured at completion: notes, checklist, service details and
 * evidence. Each section is guarded so a field with no value is omitted rather
 * than rendered empty (NAU-366). Knows nothing about permissions — management
 * affordances arrive through `fileActions` and `footer`.
 */
export function CompletionRecordDetails({
  instance,
  definition,
  evidenceLinks = "live",
  fileActions,
  footer,
}: DetailsProps) {
  const files = downloadableFiles(instance);
  const serviceDetails = serviceDetailRows(instance, definition);

  // Nothing captured and nothing to manage — render no container at all, so
  // the caller's layout gap doesn't open up around an empty block.
  if (!hasCompletionRecordDetail(instance, definition) && !footer) return null;

  return (
    <div className="flex w-full flex-col items-start gap-3">
      {hasCompletionNotes(instance) && (
        <div className="flex w-full flex-col items-start gap-1">
          <SectionLabel>Completion notes</SectionLabel>
          <span className="whitespace-pre-wrap text-body font-body text-default-font">
            {instance.completionNotes}
          </span>
        </div>
      )}

      {hasChecklistResults(instance) && (
        <div className="flex w-full flex-col items-start gap-2">
          <SectionLabel icon={<FeatherClipboard className="h-3 w-3" />}>
            Checklist
          </SectionLabel>
          <div className="flex w-full flex-col gap-2">
            {instance.checklistResults?.map((item) => (
              <div
                key={item.id}
                className="flex w-full flex-col gap-1 rounded border border-neutral-100 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-caption font-caption text-default-font">
                    {item.label}
                  </span>
                  {getChecklistResultBadge(item.result)}
                </div>
                {item.notes?.trim() && (
                  <span className="text-caption font-caption italic text-subtext-color">
                    {item.notes}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {serviceDetails.length > 0 && (
        <div className="flex w-full flex-col items-start gap-2">
          <SectionLabel>Service details</SectionLabel>
          <div className="flex w-full flex-col gap-1 rounded border border-neutral-100 px-3 py-2">
            {serviceDetails.map((row) => (
              <div
                key={row.key}
                className="flex flex-wrap items-center gap-1.5"
              >
                <span className="text-caption font-caption text-subtext-color">
                  {row.label}:
                </span>
                <span className="text-caption font-caption-bold text-default-font">
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="flex w-full flex-col items-start gap-2">
          <SectionLabel icon={<FeatherPaperclip className="h-3 w-3" />}>
            Evidence ({files.length})
          </SectionLabel>
          {evidenceLinks === "offline" && (
            <span className="text-caption font-caption text-subtext-color">
              Reconnect to view evidence.
            </span>
          )}
          <div className="flex w-full flex-col gap-1.5">
            {files.map((file) => (
              <EvidenceRow
                key={file.id}
                file={file}
                linkable={evidenceLinks === "live"}
                actions={fileActions?.(file)}
              />
            ))}
          </div>
        </div>
      )}

      {footer}
    </div>
  );
}

function EvidenceRow({
  file,
  linkable,
  actions,
}: {
  file: ComplianceTaskFileDto;
  linkable: boolean;
  actions?: ReactNode;
}) {
  const sizeLabel = formatFileSize(file.size);
  const contents = (
    <>
      {getFileIcon(file.mimeType)}
      <span className="grow shrink-0 basis-0 truncate text-caption font-caption-bold text-default-font">
        {file.fileName}
      </span>
      {sizeLabel && (
        <span className="flex-none text-caption font-caption text-subtext-color">
          {sizeLabel}
        </span>
      )}
    </>
  );

  return (
    <div
      className={`flex w-full items-center rounded border border-neutral-100 transition-colors ${
        linkable ? "hover:border-neutral-300 hover:bg-neutral-50" : ""
      }`}
    >
      {linkable ? (
        <a
          href={file.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex grow shrink-0 basis-0 cursor-pointer items-center gap-2 px-3 py-2 no-underline"
        >
          {contents}
        </a>
      ) : (
        <div className="flex grow shrink-0 basis-0 items-center gap-2 px-3 py-2 opacity-60">
          {contents}
        </div>
      )}
      {actions}
    </div>
  );
}
